import { useEffect, useRef, useState, useCallback } from 'react';

const PING_INTERVAL_MS = 25000;
const PING_TIMEOUT_MS = 4000;
const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Tracks the user's connectivity (browser online events + active backend ping).
 *
 * Returns an object:
 *   - online (bool)            current state, optimistic on mount
 *   - lastChangedAt (number)   ms timestamp of the last status change
 *   - checkNow()               manual recheck (e.g., before submit)
 */
export function useOnlineStatus() {
    const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);
    const [lastChangedAt, setLastChangedAt] = useState(Date.now());
    const lastStateRef = useRef(online);

    const setIfChanged = useCallback((next) => {
        if (lastStateRef.current !== next) {
            lastStateRef.current = next;
            setOnline(next);
            setLastChangedAt(Date.now());
        }
    }, []);

    const checkNow = useCallback(async () => {
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
            setIfChanged(false);
            return false;
        }
        try {
            const ctrl = new AbortController();
            const t = setTimeout(() => ctrl.abort(), PING_TIMEOUT_MS);
            const res = await fetch(`${API_URL}/api/auth/me`, {
                method: 'GET',
                credentials: 'include',
                signal: ctrl.signal,
                cache: 'no-store',
            });
            clearTimeout(t);
            // Any HTTP response (incl. 401) means the network is reachable.
            const ok = !!res;
            setIfChanged(ok);
            return ok;
        } catch {
            setIfChanged(false);
            return false;
        }
    }, [setIfChanged]);

    useEffect(() => {
        const handleOnline = () => { checkNow(); };
        const handleOffline = () => setIfChanged(false);
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        // Periodic ping (catches dead Wi-Fi where browser still says "online").
        const id = setInterval(() => { checkNow(); }, PING_INTERVAL_MS);
        // Initial check shortly after mount
        const t0 = setTimeout(() => { checkNow(); }, 1500);
        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
            clearInterval(id);
            clearTimeout(t0);
        };
    }, [checkNow, setIfChanged]);

    return { online, lastChangedAt, checkNow };
}

export default useOnlineStatus;
